import { useState } from 'react'

import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { columns, ACTIVITY_LOG_TYPE_OPTIONS } from './constants'
import useQueryPlayerActivityLog from '@/modules/manage-player/api/hooks/useQueryPlayerActivityLog'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { Button } from '@/common/ui/button'
import { Download } from 'lucide-react'
import { useMutationExportPlayerActivityLogCsv } from '@/modules/manage-player/api/hooks/useMutationExportPlayerActivityLogCsv'
import { DataTableMultiCheckboxFilter } from '@/common/ui/table/data-table-multi-checkbox-filter'
import { useQueryState, parseAsArrayOf, parseAsString } from 'nuqs'
import { PlayerActivityLogType } from '@/schema'
import { ExportCsvAmountModal } from '@/common/components/ExportCsvAmountModal'

interface PlayerActivityLogTableProps {
	playerId: string
}

export function PlayerActivityLogTable({
	playerId,
}: PlayerActivityLogTableProps) {
	const { pageSize, currentPage } = useTablePagination()
	const { dateFilter } = useDateFilter()
	const exportCsvMutation = useMutationExportPlayerActivityLogCsv()
	const [showExportModal, setShowExportModal] = useState(false)
	const [selectedTypes, setSelectedTypes] = useQueryState(
		'types',
		parseAsArrayOf(parseAsString)
			.withOptions({ shallow: false })
			.withDefault([]),
	)

	const { data, isLoading } = useQueryPlayerActivityLog({
		playerId,
		skip: (currentPage - 1) * pageSize,
		take: pageSize,
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
		body: {
			types:
				selectedTypes.length > 0
					? (selectedTypes as PlayerActivityLogType[])
					: undefined,
		},
	})

	const handleExportCsv = (amount: number) => {
		exportCsvMutation.mutate({
			playerId,
			startDate: dateFilter.startDate || undefined,
			endDate: dateFilter.endDate || undefined,
			take: amount || data?.total || 1000,
			skip: 0,
			types:
				selectedTypes.length > 0
					? (selectedTypes as PlayerActivityLogType[])
					: undefined,
			filename: `player_activity_log_${playerId}`,
			columnMappings: {
				type: 'ประเภทการทำรายการ',
				message: 'รายละเอียดการทำรายการ',
				note: 'หมายเหตุ',
				createdAt: 'ทำรายการเมื่อ',
			},
		})
	}

	return (
		<div className="space-y-4">
			<div className="flex flex-col items-end gap-4">
				<Button
					variant="outline"
					onClick={() => setShowExportModal(true)}
					disabled={exportCsvMutation.isPending || !data?.data?.length}
					className="flex items-center gap-2"
					size="sm"
				>
					<Download className="h-4 w-4" />
					โหลด CSV
				</Button>
				<div className="flex w-full flex-wrap items-center gap-4">
					<DataTableMultiCheckboxFilter
						options={ACTIVITY_LOG_TYPE_OPTIONS}
						selectedValues={selectedTypes}
						onSelectionChange={setSelectedTypes}
						placeholder="ประเภทกิจกรรม"
					/>
				</div>
				<DateRangePicker />
				<DateRangeButtons />
			</div>
			<DataTablePaginate
				columns={columns}
				data={data?.data || []}
				isLoading={isLoading}
				totalItems={data?.total || 0}
			/>
			<ExportCsvAmountModal
				open={showExportModal}
				onOpenChange={setShowExportModal}
				onConfirm={handleExportCsv}
				defaultAmount={data?.total || 1000}
				maxAmount={100000}
				isLoading={exportCsvMutation.isPending}
			/>
		</div>
	)
}
